<template>
    <tr class="contact-table-row">
        <td class="contact-table-row__cell contact-table-row__contact">
            <contact-avatar :contact="contact" :size="2" color="var(--color-grey-400)"></contact-avatar>

            <div class="contact-table-row__contact-info">
                <contact-full-name :contact="contact" class="contact-table-row__contact-full-name" />
                <contact-added-date :contact="contact" class="contact-table-row__contact-added-date" />
            </div>
        </td>

        <td class="contact-table-row__cell contact-table-row__identity">
            <contact-identity :contact="contact" />
        </td>

        <td
            class="contact-table-row__cell contact-table-row__segments hidden table-cell-xl"
            data-test="segments-cnt-col"
        >
            <span>
                <i
                    v-if="loadingSegmentsCount"
                    class="fa fa-spin fa-circle-o-notch"
                    data-test="segments-cnt-loading"
                />
                <template v-else>
                    {{ segmentsCount ?? '-' }}
                </template>
            </span>
        </td>

        <td class="contact-table-row__cell contact-table-row__tags hidden table-cell-xl">
            <span>{{ contact.tags.length }}</span>
        </td>

        <td class="contact-table-row__cell contact-table-row__status">
            <i v-if="loadingSubscriptions" class="fa fa-spin fa-circle-o-notch" />
            <contact-status-v2 v-else :subscriptions="subscriptions" />
        </td>

        <td class="contact-table-row__cell contact-table-row__actions">
            <vx-dropdown placement="right">
                <vx-button appearance="primary-dark-outline" data-test="contact-actions-dropdown" size="large" right="tick" :has-shadow="true">
                    Actions
                </vx-button>
                <template #items>
                    <vx-dropdown-item
                        v-for="{ label, route, event, payload, message } in contactActions"
                        :key="label"
                        :href="route"
                        :data-test="`action-${label.toLowerCase()}`"
                        data-router="client-route"
                        @click.prevent="onActionClick({ route, event, payload, message })"
                        >{{ label }}</vx-dropdown-item
                    >
                </template>
            </vx-dropdown>
        </td>
    </tr>
</template>

<script setup>
import { ref, computed } from 'vue';

import ContactAvatar from '../shared/ContactAvatar.vue';

import ContactFullName from '../shared/ContactFullName.vue';
import ContactAddedDate from '../shared/ContactAddedDate.vue';
import ContactIdentity from '../shared/ContactIdentity.vue';
import ContactStatusV2 from '../shared/ContactStatusV2.vue';

import { VxButton, VxDropdown, VxDropdownItem } from '@voxie/frontend-components-v3';

import { showClientRoute, clearClientRoute } from '../../../composables/useClientRoutes';
import { contactsClient } from '../../../services';

const props = defineProps({
    contact: {
        type: Object,
        required: true,
    },
});

const teamId = Spark.state.currentTeam.id;
const loadingSegmentsCount = ref(true);
const loadingSubscriptions = ref(true);
const segmentsCount = ref(null);
const subscriptions = ref([]);

const contactActions = computed(() => {
    const actions = [
        { label: 'View', route: `#/show/${props.contact.id}`, event: 'contactShow', payload: props.contact.id },
        { label: 'Edit', route: `#/edit/${props.contact.id}`, event: 'contactEdit', payload: props.contact.id },
    ];

    if (['opt_in', 'unknown'].includes(marketingSubscriptionStatus.value)) {
        actions.push({
            label: 'Unsubscribe',
            route: '',
            event: 'contactUnsubscribe',
            payload: props.contact.id,
            message: 'Are you sure? This contact will not receive any marketing messages.',
        });
    }

    if (['opt_out', 'unknown'].includes(marketingSubscriptionStatus.value)) {
        actions.push({
            label: 'Subscribe',
            route: '',
            event: 'contactSubscribe',
            payload: props.contact.id,
            message: 'Are you sure? This contact will begin to receive marketing messages, if able.',
        });
    }

    actions.push({
        label: 'Delete',
        route: '',
        event: 'contactDelete',
        payload: props.contact.id,
        message: 'Are you sure? This contact and all tags, attributes, events etc will be deleted. There is no undo.',
    });

    return actions;
});

const onActionClick = ({ route, event, payload, message }) => {
    if (message && !window.confirm(message)) {
        return;
    }

    Bus.$emit(event, payload);

    route ? showClientRoute(route) : clearClientRoute();
};

contactsClient
    .contactsSegmentsCount(teamId, props.contact.id)
    .then(
        ({ data }) => {
            segmentsCount.value = data.count;
        },
        () => {
            //
        }
    )
    .finally(() => {
        loadingSegmentsCount.value = false;
    });

const marketingSubscriptionStatus = computed(() => subscriptions.value.find(sub => sub.type === 'marketing')?.status || 'unknown');

const getSubscriptions = () => {
    contactsClient
    .getContactSubscriptionStatus(teamId, props.contact.id)
    .then(({ data }) => {
        subscriptions.value = data.data;

        const hasMarketingSubscription = !!subscriptions.value.find(subscription => subscription.type === 'marketing');
        const hasTransactionalSubscription = !!subscriptions.value.find(subscription => subscription.type === 'transactional');

        if (!hasMarketingSubscription) {
            subscriptions.value.push({
                type: 'marketing',
                status: 'unknown',
                reason_text: '-',
                updated_at: props.contact.created_at
            });
        }

        if (!hasTransactionalSubscription) {
            subscriptions.value.push({
                type: 'transactional',
                status: 'unknown',
                reason_text: '-',
                updated_at: props.contact.created_at
            });
        }
    })
    .finally(() => {
        loadingSubscriptions.value = false;
    });
}

getSubscriptions();

Bus.$on(`contactPending-${props.contact.id}`, (isPending) => {
    if(!isPending) {
        getSubscriptions();
    }
});
</script>

<style lang="scss">
.contact-table-row {
    &:nth-of-type(odd) {
        background-color: var(--color-background);
    }
}

.contact-table-row__cell {
    padding: 16px;
}

.contact-table-row__contact {
    display: flex;
    align-items: center;
    gap: 16px;
    max-width: 400px;
}

.contact-table-row__contact-info {
    display: flex;
    flex-direction: column;
    max-width: calc(100% - 48px);
}

.contact-table-row__contact-full-name {
    margin: 0;
}

.contact-table-row__contact-added-date {
    margin: 0;
}

.contact-table-row__identity {
    max-width: 300px;
}

.contact-table-row__segments,
.contact-table-row__tags {
    font-family: var(--font-open-sans);
    font-weight: var(--weight-regular);
    font-size: 0.9375rem;
    color: var(--color-grey);
    text-align: right;
}

.contact-table-row__status {
    text-align: left;
}

.contact-table-row__contact-status {
    display: flex;
    justify-content: center;
    width: 100%;
}

.contact-table-row__actions {
    text-align: center;
}
</style>
